import { reactive, computed, watch, getCurrentInstance } from "vue";
import { useActions, useAudioNotification, useGetters, useMisc, useState } from "@/composables";

// Singleton state - shared across all component instances
let isInitialized = false;
let globalEmitter = null;

const minicart = reactive({
    timeoutInst: false,
    secondsLeft: 0,
    warn2MinShown: false,
    warn5MinShown: false,
    extendCartShown: false,
    clearing: false,
});

/**
 * Composable for cart countdown timer
 * Uses singleton pattern to ensure timer only runs once across all component instances
 */
export function useCartCountdown() {
    const { clearCartApiByTimer } = useActions();
    const { playPrimedAudio } = useAudioNotification();
    const { getCurrentCartTypes } = useGetters();
    const { cartMeta } = useState();
    const { clearCartComments } = useMisc();

    // Get emitter from instance if not already set
    if (!globalEmitter) {
        const instance = getCurrentInstance();
        if (instance) {
            globalEmitter = instance.appContext.config.globalProperties.emitter;
        }
    }

    const countdownText = computed(() => {
        if (minicart.secondsLeft) {
            const minutes = Math.floor(minicart.secondsLeft / 60)
                .toString()
                .padStart(2, "0");
            const seconds = Math.floor(minicart.secondsLeft % 60)
                .toString()
                .padStart(2, "0");
            return `${minutes}:${seconds}`;
        } else {
            return ``;
        }
    });

    const countDownTimer = () => {
        if (minicart.timeoutInst) {
            clearTimeout(minicart.timeoutInst);
            minicart.timeoutInst = null;
        }
        if (cartMeta.value.timer.secondsLeft) {
            minicart.timeoutInst = setTimeout(() => {
                const start = new Date(cartMeta.value.timer.localTime);
                const now = new Date();
                const secondsElapsed = (now - start) / 1000;
                let all_site_settings = JSON.parse(localStorage.getItem("site_settings"));
                
                if (!secondsElapsed) {
                    minicart.secondsLeft = cartMeta.value.timer.secondsLeft;
                } else {
                    if (secondsElapsed < cartMeta.value.timer.secondsLeft) {
                        minicart.secondsLeft = cartMeta.value.timer.secondsLeft - secondsElapsed;
                        
                        if (
                            cartMeta.value.extendCartTimer &&
                            !minicart.extendCartShown &&
                            minicart.secondsLeft >= all_site_settings.extend_cart_timer_warning - 5 &&
                            minicart.secondsLeft <= all_site_settings.extend_cart_timer_warning
                        ) {
                            minicart.extendCartShown = true;
                            if (globalEmitter) {
                                globalEmitter.emit("displayExtendCartWarningModal", {
                                    header: 'Extend Cart Timer',
                                    message: all_site_settings.extend_cart_timer_message
                                });
                            }
                            playPrimedAudio(all_site_settings);
                        }

                        const inWarningWindow = (warningTimer) => {
                            const threshold = Number(warningTimer);
                            if (!Number.isFinite(threshold)) {
                                return false;
                            }
                            return minicart.secondsLeft <= threshold && minicart.secondsLeft >= threshold - 5;
                        };

                        if (
                            cartMeta.value.expiresByCutoff === true &&
                            !minicart.warn2MinShown &&
                            inWarningWindow(all_site_settings.cart_cutoff_warning_timer)
                        ) {
                            minicart.warn2MinShown = true;
                            if (globalEmitter) {
                                globalEmitter.emit("displayExpiringCartWarningModal", {
                                    header: 'Cutoff is expiring',
                                    message: all_site_settings.cart_cutoff_warning_message,
                                    program_codes: getCurrentCartTypes.value
                                });
                            }
                            playPrimedAudio(all_site_settings);
                        } else if (
                            cartMeta.value.expiresByCutoff === false &&
                            !minicart.warn5MinShown &&
                            inWarningWindow(all_site_settings.cart_expiration_warning_timer)
                        ) {
                            minicart.warn5MinShown = true;
                            if (globalEmitter) {
                                globalEmitter.emit("displayExpiringCartWarningModal", {
                                    header: 'Cart is expiring',
                                    message: all_site_settings.cart_expiration_warning_message,
                                    program_codes: getCurrentCartTypes.value
                                });
                            }
                            playPrimedAudio(all_site_settings);
                        }

                        countDownTimer();
                    } else if (!minicart.clearing) {
                        minicart.clearing = true;
                        if (globalEmitter) {
                            globalEmitter.emit('showLoader');
                        }
                        minicart.secondsLeft = 0;
                        Promise.resolve(clearCartApiByTimer()).finally(() => {
                            minicart.clearing = false;
                        });
                        clearCartComments();
                        if (globalEmitter) {
                            globalEmitter.emit('hideLoader');
                        }
                    }
                }
            }, 1000);
        } else {
            if (minicart.timeoutInst) {
                clearTimeout(minicart.timeoutInst);
                minicart.timeoutInst = null;
            }
        }
    };

    const initializeTimer = () => {
        if (!isInitialized) {
            isInitialized = true;

            watch(() => cartMeta.value.timer.secondsLeft, () => {
                countDownTimer();
            });

            countDownTimer();
        }
    };

    return {
        countdownText,
        minicart,
        initializeTimer
    };
}
